import React, { useState } from "react";

import styles from "./styles.module.scss";
import { OnNavigationType } from "../_basic/SlideNavigation";
import { InputField, SeparatedFormSlideType } from "../../../types/form";

interface SeparatedFormSlideProps {
  data: SeparatedFormSlideType;
  onNavigation: OnNavigationType;
  submitData?: () => void;
  setPayloadValue: any;
  sendAnalyticsData: any;
}

const SeparateFormSlide: React.FC<SeparatedFormSlideProps> = ({
  data,
  onNavigation,
  submitData,
  setPayloadValue,
  sendAnalyticsData,
}) => {
  const [inputValue, setInputValue] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMarketingConsentChecked, setIsMarketingConsentChecked] =
    useState(false);
  const [isThirdPartyMarketingChecked, setIsThirdPartyMarketingChecked] =
    useState(false);
  const [isThirdPartyProfilingChecked, setIsThirdPartyProfilingChecked] =
    useState(false);

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/;
  const match = data?.privacyPolicyText?.match(linkRegex);

  const handleMarketingConsentChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIsMarketingConsentChecked(e.target.checked);
  };
  const handleThirdPartyMarketingChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIsThirdPartyMarketingChecked(e.target.checked);
  };

  const handleThirdPartyProfilingChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIsThirdPartyProfilingChecked(e.target.checked);
  };

  const privacyPolicyContent = match
    ? {
        before: data.privacyPolicyText.split(match[0])[0],
        linkText: match[1],
        linkUrl: match[2],
        after: data.privacyPolicyText.split(match[0])[1],
      }
    : { before: data.privacyPolicyText, linkText: "", linkUrl: "", after: "" };

  const validateInput = (field: InputField, value: string) => {
    let error = "";

    if (field.errorMessages?.required && !value) {
      error = field.errorMessages.required;
    } else if (field.type === "email") {
      if (!emailRegex.test(value)) {
        error = field.errorMessages?.invalid || "Invalid email format";
      }
    } else if (field.type === "phone") {
      if (value.length === 0) {
        error = field.errorMessages?.invalid || "Invalid phone number.";
      }
    } else if (field.type === "name") {
      const nameParts = value.trim().split(" ");
      if (nameParts.length < 2) {
        error =
          field.errorMessages?.required || "Please enter first and last name.";
      }
    }

    return error;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: InputField
  ) => {
    const { name, value } = e.target;
    if (field.type === "phone" && !/^[\d+\s-]*$/.test(value)) {
      return;
    }

    setInputValue((prev) => ({ ...prev, [name]: value }));
    const validationError = validateInput(field, value);
    setErrors((prev) => ({ ...prev, [name]: validationError }));
  };

  const isFormValid = () => {
    const hasErrors = Object.values(errors).some((error) => error !== "");

    const allFieldsFilled = data?.inputs?.every(
      (input) => inputValue[input.fieldName] || !input.errorMessages?.required
    );

    if (data.id !== "phoneFormSlide") {
      return allFieldsFilled && !hasErrors;
    }

    const conditions = [
      {
        mandatory: data?.marketingAndProfiling?.mandatory,
        check: isMarketingConsentChecked,
      },
      {
        mandatory: data?.thirdPartyMarketing?.mandatory,
        check: isThirdPartyMarketingChecked,
      },
      {
        mandatory: data?.thirdPartyProfiling?.mandatory,
        check: isThirdPartyProfilingChecked,
      },
    ];

    const areMandatoryChecksValid = conditions.every(
      (condition) => !condition.mandatory || condition.check
    );

    return allFieldsFilled && !hasErrors && areMandatoryChecksValid;
  };

  const handleFormSubmit = () => {
    data.inputs?.forEach((input) => {
      const value = inputValue[input.fieldName];
      if (input.type === "name") {
        const [firstName, ...lastNameArray] = value.split(" ");
        const lastName = lastNameArray.join(" ");
        setPayloadValue("contact[first_name]", firstName);
        setPayloadValue("contact[last_name]", lastName);
        if (data.id === "nameFormSlide") {
          sendAnalyticsData("contact_form", "name");
          sendAnalyticsData("contact_form_nameFormSlide", "continue_button");
        }
      } else if (input.type === "radio") {
      } else {
        setPayloadValue(`contact[${input.fieldName}]`, value);
        setPayloadValue("enpalMarketingConsent", isMarketingConsentChecked);
        setPayloadValue("thirdPartyMarketing", isThirdPartyMarketingChecked);
        setPayloadValue("thirdPartyProfiling", isThirdPartyProfilingChecked);
        if (data.id === "emailFormSlide") {
          sendAnalyticsData("contact_form", "email");
          sendAnalyticsData("contact_form_emailFormSlide", "continue_button");
        }
        if (data.id === "phoneFormSlide") {
          sendAnalyticsData("contact_form", "phone");
          sendAnalyticsData("contact_form_phoneFormSlide", "continue_button");
        }
      }
    });

    if (data?.id === "phoneFormSlide" && submitData) {
      submitData();
    }
    onNavigation(1);
  };

  const renderSalutation = (field: InputField) => {
    if (!field || !field.options) return null;
  };

  function processTextWithIndent(content: any) {
    if (!content) return [];
    return content.split("\n").map((line: any, index: any) => {
      if (line.trim().startsWith("-")) {
        return (
          <p key={index} className={styles.tickItem}>
            ✔ {line.trim().slice(1).trim()}
          </p>
        );
      }
      return <p key={index}>{line}</p>;
    });
  }

  const renderField = (field: InputField) => {
    return (
      <div className={styles.inputGroup} key={field.fieldName}>
        <div className={styles.errorLabel}>
          <label className={styles.inputLabels} htmlFor={field.fieldName}>
            {field.label}
          </label>
          {errors[field.fieldName] && (
            <div className={styles.errorMessages}>
              <p className={styles.errorText}>{errors[field.fieldName]}</p>
            </div>
          )}
        </div>
        <input
          className={`${styles.inputField} ${
            errors[field.fieldName] && styles.errorBorder
          }`}
          type={field.type}
          onChange={(e) => handleInputChange(e, field)}
          id={field.fieldName}
          data-testid="form_input"
          name={field.fieldName}
          placeholder={field.placeholder}
          value={inputValue[field.fieldName] || ""}
        />
      </div>
    );
  };

  const Modal: React.FC<{ onClose: () => void }> = ({ onClose }) => (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContainer}>
        <button onClick={onClose} className={styles.closeButton}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            height="24"
            width="25"
            role="presentation"
            aria-labelledby="icon-arrow-left"
            fill="none"
            focusable="false"
            strokeLinecap="round"
            strokeLinejoin="round"
            stroke="currentColor"
            strokeWidth="1.5"
            className={styles.closeButton}>
            <title id="close">close</title>
            <g>
              <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </g>
          </svg>
        </button>
        <div className={styles.modalTitle}>{data.infoBox?.title}</div>
        <div className={styles.modalContent}>
          {processTextWithIndent(data.infoBox?.content)}
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles.personalInfoFormContainer}>
      {data?.estimatedSavingsSubText && data?.estimatedSavingsText && (
        <div className={styles.headingContainer}>
          {data?.estimatedSavingsSubText && (
            <div>{data.estimatedSavingsSubText}</div>
          )}
          {data?.estimatedSavingsText && (
            <div className={styles.estimatedSavingsText}>
              {data?.estimatedSavingsText}
              <button
                className={styles.modalButton}
                onClick={() => setIsModalOpen(true)}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  height="15"
                  width="15"
                  role="presentation"
                  aria-labelledby="icon-arrow-left"
                  fill="none"
                  focusable="false"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  className={styles.modalButton}>
                  <title id="info">info</title>
                  <g>
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <circle cx="12" cy="12" r="9"></circle>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                    <polyline points="11 12 12 12 12 16 13 16"></polyline>
                  </g>
                </svg>
              </button>
            </div>
          )}
        </div>
      )}
      {data?.savingsIndicator && (
        <div className={styles.progressBar}>
          <div>{data.savingsIndicator.label}</div>
          <div className={styles.rightBar}>
            {data.savingsIndicator.veryHighLabel}
            <img
              className={styles.bar}
              alt="savings-indicator"
              src="https://slider-assets.cust.enpal.io/slider-images/teaser-progress-1.png"
            />
          </div>
        </div>
      )}
      <div className={styles.lowerContainer}>
        <div className={styles.title}>{data.title}</div>
        <form className={styles.formElement}>
          {data.inputs?.map((input) =>
            input.type === "radio"
              ? renderSalutation(input)
              : renderField(input)
          )}
        </form>
        {data.navigation && (
          <button
            className={`${styles.nextButton} ${
              !isFormValid() && styles.disabledButton
            }`}
            disabled={!isFormValid()}
            onClick={handleFormSubmit}>
            {data.navigation.nextButtonLabel || "Submit"}
            {data.navigation.nextButtonArrow && <span>→</span>}
          </button>
        )}
        {data.privacyPolicyText && (
          <div className={styles.infoBox}>
            {privacyPolicyContent.before}
            <a
              className={styles.linkAttached}
              href={privacyPolicyContent.linkUrl}
              target="_blank"
              rel="noopener noreferrer">
              {privacyPolicyContent.linkText}
            </a>
            {privacyPolicyContent.after}
          </div>
        )}
        <div className={styles.marketingContainer}>
          {data.marketingAndProfiling && (
            <>
              <div className={styles.checkBoxTitle}>
                {data.marketingAndProfiling.title}
              </div>
              <div className={styles.marketingCheckBox}>
                <label
                  className={`${styles.customCheckbox} ${styles.checkBox}`}>
                  <input
                    type="checkbox"
                    onChange={handleMarketingConsentChange}
                    checked={isMarketingConsentChecked}
                  />
                  <span className={styles.checkbox}></span>
                </label>
                <div className={styles.text}>
                  {data.marketingAndProfiling.description}
                </div>
              </div>
            </>
          )}
          {data.thirdPartyMarketing && (
            <div className={styles.consentContainer}>
              <div className={styles.consentOption}>
                <div className={styles.checkBoxTitle}>
                  {data.thirdPartyMarketing.title}
                </div>
                <label className={styles.checkBox}>
                  <input
                    type="checkbox"
                    onChange={handleThirdPartyMarketingChange}
                    checked={isThirdPartyMarketingChecked}
                  />
                  {data.thirdPartyMarketing.description}
                </label>
              </div>
            </div>
          )}
          {data.thirdPartyProfiling && (
            <div className={styles.consentContainer}>
              <div className={styles.consentOption}>
                <div className={styles.checkBoxTitle}>
                  {data.thirdPartyProfiling.title}
                </div>
                <label className={styles.checkBox}>
                  <input
                    type="checkbox"
                    onChange={handleThirdPartyProfilingChange}
                    checked={isThirdPartyProfilingChecked}
                  />
                  {data.thirdPartyProfiling.description}
                </label>
              </div>
            </div>
          )}

          {data.importantNote && (
            <div className={styles.consentContainer}>
              <div className={styles.consentOption}>
                <div className={styles.checkBoxTitle}>
                  {data.importantNote.title}
                </div>
                <label className={styles.checkBox}>
                  {data.importantNote.description}
                </label>
              </div>
            </div>
          )}
          {data.consentRevocation && (
            <div className={styles.consentContainer}>
              <div className={styles.consentOption}>
                <div className={styles.checkBoxTitle}>
                  {data.consentRevocation.title}
                </div>
                <label className={styles.checkBox}>
                  {data.consentRevocation.description}
                </label>
              </div>
            </div>
          )}
        </div>
        {isModalOpen && <Modal onClose={() => setIsModalOpen(false)} />}
      </div>
    </div>
  );
};

export default SeparateFormSlide;
